import { Injectable } from '@angular/core';
import { ConvertHelper } from '@barthauer/abc/core';
import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  ReadListDataSourceModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import { exportDataGrid as exportExcelDataGrid } from 'devextreme/excel_exporter';
import { exportDataGrid as exportPdfDataGrid } from 'devextreme/pdf_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { exhaustMap, map } from 'rxjs/operators';
import { StorageEnum } from '../models/enumeration-list.model';
import { BibCategory, BibQuestion, BibResponse, ObjectTypes} from '@barthauer/custom/xmt-models';

//------------------------------------- Persist Multiple
export interface persistMultipleInputType {
  insert?: insertMultipleInputType[];
  update?: updateMultipleInputType[];
  delete?: deleteMultipleInputType[];
}

export interface insertMultipleInputType {
  modelName: string;
  data: any[];
  SeqNr?: number;
}
export interface updateMultipleInputType {
  modelName: string;
  data: any[];
}
export interface deleteMultipleInputType {
  modelName: string;
  data: string[];
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly objectSpaceName = 'ABC.Applications.KAOS.Data';
  public cachedCategories: BibCategory[];
  public cachedQuestions: BibQuestion[];
  public cachedResponses: BibResponse[];
  public cachedStatus = [];
  public cachedCompaniesTitles = [];
  public res = ResourceManager.getResources('dt');
  public companyData: Map<string, string> = new Map<string, string>();
  public companyName = new Subject<string>();
  public companyName$ = this.companyName.asObservable();
  public favoriteData: Map<string, string> = new Map<string, string>();
  public favoriteName = new Subject<string>();
  public favoriteName$ = this.favoriteName.asObservable();
  private columnsReadySubject :  BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public columnsReady$ : Observable<boolean> = this.columnsReadySubject.asObservable();
  constructor(private coreDataService: CoreDataService, private persistenceService: PersistenceService) { }

  public loadListDataSource(objectType: string, columnName: string): Observable<any> {
    const readListDataSourceQuery = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = objectType;
    readListDataSourceQuery.ObjectKey = objectKey;
    readListDataSourceQuery.OutputMode = 0;
    readListDataSourceQuery.ColumnName = columnName;
    return this.coreDataService.executeReadListDataSource(readListDataSourceQuery);
  }

  public readObjects(objectType: string, columns: QueryColumn[], OPath?: string): Observable<any> {
    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = objectType;
    mainQuery.Columns = columns;
    mainQuery.OPath = OPath;
    if (OPath) {
      mainQuery.OPath = OPath;
    }
    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService.executeReadObjectsQuery(readObjectQuery);
  }

  public persistObject(
    mode: PersistMode,
    objectKey: ObjectKeys,
    columns?: TargetColumnValue[],
    id?: string
  ): Observable<ArrayBuffer> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();
    persistQuery.ObjectSpaceName = this.objectSpaceName;
    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = mode;
    targetObject.ObjectKey = new ObjectKey();
    if (mode === PersistMode.Update) {
      targetObject.ObjectKey.Id = id;
    }
    targetObject.ObjectKey.ObjectType = objectKey;
    targetObject.TargetColumns = columns;
    persistQuery.Object = targetObject;
    return this.persistenceService.executePersistObjectQuery(persistQuery);
  }

  public prepareCompanyData(id, companyName) {
    if (this.companyData) {
      this.companyData.set(StorageEnum.CompanyId, id);
      this.companyData.set(StorageEnum.CompanyName, companyName);
      this.companyName.next(companyName);
    }
  }

  public setFavoriteData(favoriteName: string, favoriteData: string) {
    this.favoriteData.set(favoriteName, favoriteData);
    this.favoriteName.next(favoriteName);
  }

  public onExporting = (e, fileName: string): void => {
    if (e.format === 'xlsx') {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(fileName);
      exportExcelDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName + '.' + e.format);
        });
      });
    } else {
      const doc = new jsPDF();
      exportPdfDataGrid({
        jsPDFDocument: doc,
        component: e.component,
        indent: 5,
      }).then(() => {
        doc.save(fileName + '.' + e.format);
      });
    }
  };

  // --------------------------- Pick List

  public getPickListByName(name: string, language: number = 1031): Observable<{ Id: string }[]> {
    const controlItemsColumns = [this.coreDataService.createQueryColumn('Id', 'Id')];
    const opath = `Name='${name}' AND Language='${language}'`;
    return this.readObjects(ObjectKeys.PICKLIST, controlItemsColumns, opath);
  }

  public getPickListtemsByListId(itemId: string): Observable<{ Id: string; Value: string; Description: string }[]> {
    const controlItemsColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Value', 'Value'),
      this.coreDataService.createQueryColumn('Description', 'Description', QueryColumnSortOrder.Ascending),
    ];
    const opath = `ParentId='${itemId}'`;
    return this.readObjects(ObjectKeys.PICKLISTITEM, controlItemsColumns, opath);
  }

  public getPickListItemsByPickListName(
    pickListName: string,
    language: number = 1031,
    opath?: string
  ): Observable<{ Id: string; Value: string; Description: string }[]> {
    const controlItemsColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Value', 'Value'),
      this.coreDataService.createQueryColumn('Description', 'Description', QueryColumnSortOrder.Ascending),
    ];
    return this.getPickListByName(pickListName, language).pipe(
      exhaustMap((pickList) => {
        let filter = `ParentId='${pickList[0].Id}'`;
        if (opath) filter = `${filter} AND ${opath}`;
        return this.readObjects(ObjectKeys.PICKLISTITEM, controlItemsColumns, filter);
      })
    );
  }

  // --------------------------- Persist single

  private getTargetColumnValue(data: Object): TargetColumnValue[] {
    const queryColumns: TargetColumnValue[] = [];
    Object.entries(data).forEach(([property, value]) => {
      if (value !== undefined && this.isPrimitive(value) && property !== 'Id') {
        queryColumns.push({ Name: property, Value: value });
      }
    });
    return queryColumns;
  }

  private isPrimitive(test): boolean {
    return test !== Object(test);
  }

  public updateObject<T>(objectKey: ObjectKeys, id: string, data: T): Observable<IPersistResult> {
    if (!objectKey || !data || !id) {
      return of(null);
    }

    const persistQuery: PersistObjectModel = new PersistObjectModel();

    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Update;

    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id;
    targetObject.ObjectKey.ObjectType = objectKey;

    targetObject.TargetColumns = this.getTargetColumnValue(data);

    persistQuery.Object = targetObject;
    return this.persistenceService
      .executePersistObjectQuery(persistQuery)
      .pipe(map((data) => ConvertHelper.arrayBufferToObject(data)));
  }

  public insertObject<T>(objectKey: ObjectKeys, data: T): Observable<IPersistResult> {
    if (!objectKey || !data) {
      return of(null);
    }

    const persistQuery: PersistObjectModel = new PersistObjectModel();

    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Insert;

    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.ObjectType = objectKey;

    targetObject.TargetColumns = this.getTargetColumnValue(data);

    persistQuery.Object = targetObject;
    return this.persistenceService
      .executePersistObjectQuery(persistQuery)
      .pipe(map((data) => ConvertHelper.arrayBufferToObject(data)));
  }

  public deleteObject(objectKey: ObjectKeys, id: string): Observable<IPersistResult> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();
    persistQuery.ObjectSpaceName = this.objectSpaceName;
    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Delete;
    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id;
    targetObject.ObjectKey.ObjectType = objectKey;
    persistQuery.Object = targetObject;
    return this.persistenceService
      .executePersistObjectQuery(persistQuery)
      .pipe(map((data) => ConvertHelper.arrayBufferToObject(data)));
  }

  // --------------------------- Persist Multiple Objects

  public persistObjects(
    objectKey: ObjectTypes | ObjectKeys,
    persistMode: PersistMode,
    data: unknown[],
    keyExprs: string[],
    id?: string
  ): Observable<any> {
    const query: PersistObjectsModel = new PersistObjectsModel();
    const objectsPersistQuery: TargetObjectData[][] = [[]];
    data?.forEach((object) => {
      const objectData: TargetObjectData = new TargetObjectData();
      objectData.Mode = persistMode;
      objectData.ObjectKey = new ObjectKey();
      if (persistMode === PersistMode.Update) {
        objectData.ObjectKey.Id = object['Id'];
      }
      objectData.ObjectKey.ObjectType = objectKey;
      const objectColumns: TargetColumnValue[] = [];
      keyExprs.forEach((keyExpr) => {
        objectColumns.push({ Name: keyExpr, Value: object[keyExpr] });
      });
      objectData.TargetColumns = objectColumns;
      objectsPersistQuery[0].push(objectData);
    });
    query.Objects = objectsPersistQuery;
    return this.coreDataService.executePersistObjectsQuery(query);
  }
  public setColumnsReady(value: boolean): void {
    this.columnsReadySubject.next(value);
  }
}

/**
 * These parameters are required for persistence requests
 * @param AUDIT  AuditDate, Designation, ParentId (Address id)
 * @param ADDRESS  Name, Status, MatchCode
 * @param AUDITITEM  Score
 * @param BIBCATEGORY  Designation
 * @param BIBMEAUSRE
 * @param BIBQUESTION  Designation
 * @param BIBRESPONSE  Designation
 * @param LIBRARY  Designation
 * @param MEAUSRE  AuditDate, Designation
 * @param VARIANT  Designation
 * @param VARIANTITEM
 */
export enum ObjectKeys {
  AUDIT = 'AudAudit',
  ADDRESS = 'Address',
  AUDITITEM = 'AudAuditItem',
  BIBCATEGORY = 'AudBibCategory',
  BIBMEAUSRE = 'AudBibMeasure',
  BIBQUESTION = 'AudBibQuestion',
  BIBRESPONSE = 'AudBibResponse',
  LIBRARY = 'AudLibrary',
  MEAUSRE = 'AudMeasure',
  VARIANT = 'AudVariant',
  Person = 'Person',
  VARIANTITEM = 'AudVariantItem',
  TASK = 'Task',
  PICKLIST = 'Picklist',
  PICKLISTITEM = 'PicklistItem',
  COMMENT = 'Comment',
  BOX = 'Box',
  BOXDETAIL = 'BoxDetail',
}
export interface IPersistResult {
  Id: string;
  ObjectType: string;
}
